import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import SubscribeCompareModal from '@components/dashboard/modals/CompareTamponsModal';
import Colors from '@components/shared/Colors';
import Select from '@components/shared/Select';
import { HEMP_SVG, SOLID_TICK_SVG } from '@constants';
import { BoxState } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import IconTampon from '@images/icon-tampon-clear.svg';
import QuestionImage from '@images/storefront/question-gold-bg.svg';
import { getTamponConfigAndCounts } from '@utils/product';

const presetBoxes = {
  '18': [
    {
      label: 'Super Box (9 Coated + 9 Nude)',
      value: '0/9/0/9',
      img: HEMP_SVG
    },
    {
      label: 'Regular Box (9 Coated + 9 Nude)',
      value: '9/0/9/0',
      img: HEMP_SVG
    },
    {
      label: 'Coated Box (9 Regular + 9 Super)',
      value: '9/9/0/0',
      img: HEMP_SVG
    },
    {
      label: 'Nude Box (9 Regular + 9 Super)',
      value: '0/0/9/9'
    },
    {
      label: '18 Coated Super Box',
      value: '0/18/0/0',
      img: HEMP_SVG
    },
    {
      label: '18 Coated Regular Box',
      value: '18/0/0/0',
      img: HEMP_SVG
    },
    {
      label: '18 Nude Super Box',
      value: '0/0/0/18'
    },
    {
      label: '18 Nude Regular Box',
      value: '0/0/18/0'
    }
  ],
  '36': [
    {
      label: 'Super Box (18 Coated + 18 Nude)',
      value: '0/18/0/18',
      img: HEMP_SVG
    },
    {
      label: 'Regular Box (18 Coated + 18 Nude)',
      value: '18/0/18/0',
      img: HEMP_SVG
    },
    {
      label: 'Coated Box (18 Regular + 18 Super)',
      value: '18/18/0/0',
      img: HEMP_SVG
    },
    {
      label: 'Nude Box (18 Regular + 18 Super)',
      value: '0/0/18/18'
    },
    {
      label: '36 Coated Super Box',
      value: '0/36/0/0',
      img: HEMP_SVG
    },
    {
      label: '36 Coated Regular Box',
      value: '36/0/0/0',
      img: HEMP_SVG
    },
    {
      label: '36 Nude Super Box',
      value: '0/0/0/36'
    },
    {
      label: '36 Nude Regular Box',
      value: '0/0/36/0'
    }
  ],
  '54': [
    {
      label: 'Super Box (27 Coated + 27 Nude)',
      value: '0/27/0/27',
      img: HEMP_SVG
    },
    {
      label: 'Regular Box (27 Coated + 27 Nude)',
      value: '27/0/27/0',
      img: HEMP_SVG
    },
    {
      label: 'Coated Box (27 Regular + 27 Super)',
      value: '27/27/0/0',
      img: HEMP_SVG
    },
    {
      label: 'Nude Box (27 Regular + 27 Super)',
      value: '0/0/27/27'
    },
    {
      label: '54 Coated Super Box',
      value: '0/54/0/0',
      img: HEMP_SVG
    },
    {
      label: '54 Coated Regular Box',
      value: '54/0/0/0',
      img: HEMP_SVG
    },
    {
      label: '54 Nude Super Box',
      value: '0/0/0/54'
    },
    {
      label: '54 Nude Regular Box',
      value: '0/0/54/0'
    }
  ]
};

interface Props {
  boxStateOuter: BoxState;
  setBoxStateOuter: (value: BoxState) => void;
}

const TamponSelectionPresetDropdown: React.FC<Props> = ({ boxStateOuter, setBoxStateOuter }) => {
  const { country } = useCountry();
  const [modalOpen, setModalOpen] = useState(false);
  const [boxState, setBoxState] = useState<BoxState>(boxStateOuter);
  const [optionSelected, setOptionSelected] = useState();

  useEffect(() => {
    if (boxState.boxSize < 18) {
      updateBoxSize(18);
    }
    if (presetBoxes[boxState.boxSize]) {
      setOptionSelected(presetBoxes[boxState.boxSize] && presetBoxes[boxState.boxSize][0]);
    }
    setBoxStateOuter(boxState);
  }, [boxState]);

  const updateBoxSize = (size: number) => {
    const updatedBox = presetBoxes[size] && presetBoxes[size][0];
    updateBoxConfig(updatedBox.value);
  };

  const updateBoxConfig = (value) => {
    const { config, count } = getTamponConfigAndCounts(value);
    setBoxState({ ...config, boxSize: count });
  };

  const handleOptionSelect = (value) => {
    updateBoxConfig(value);
  };

  const renderBoxSizeTab = (size: number) => (
    <Tab key={size} selected={boxState.boxSize === size} onClick={() => updateBoxSize(size)}>
      {boxState.boxSize === size && <img src={SOLID_TICK_SVG} />}
      <span>{size}</span>
      <IconTampon />
    </Tab>
  );

  return (
    <>
      <Container>
        <Tabs>
          {Object.keys(presetBoxes).map((key: string) => renderBoxSizeTab(Number(key)))}
          <OpenCompare onClick={() => setModalOpen(true)}>
            <QuestionImage />
          </OpenCompare>
        </Tabs>
        <Select
          style={{ marginTop: 0, marginBottom: 0, width: '100%', alignSelf: 'center' }}
          options={presetBoxes[boxState.boxSize.toString()]}
          defaultValue={optionSelected}
          onSelectCallback={async (option) => {
            handleOptionSelect(option.value);
          }}
          hasSelectedIndicator={true}
        ></Select>
      </Container>
      <SubscribeCompareModal modalOpen={modalOpen} closeModal={() => setModalOpen(false)} country={country} />
    </>
  );
};

export default TamponSelectionPresetDropdown;

interface StyleProps {
  color?: string;
  textColor?: string;
  selected?: boolean;
}

const Container = styled.div`
  color: ${Colors.forestGreen};
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${Colors.cream};

  .select-container > div:first-child {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
`;

const Tabs = styled.div`
  color: ${Colors.forestGreen};
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 40px;
  border: 1px solid ${Colors.paleGold};
  border-bottom-color: ${Colors.ecru};
  border-top-left-radius: 2px;
  border-top-right-radius: 2px;
  margin-bottom: -1px;
`;

const Tab = styled.button<StyleProps>`
  color: ${Colors.forestGreen};
  all: unset;
  display: flex;
  height: 100%;
  min-width: 56px;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  border-bottom: 3px solid ${(props) => (props.selected ? Colors.gold : Colors.ecru)};
  flex-direction: 'row';
  gap: 5px;
  font-family: BrownStd;
  font-size: 14px;
  font-weight: ${(props) => (props.selected ? '700' : '400')};
  line-height: 17px;
  text-align: center;

  img::first-child {
    width: 6px;
  }

  svg {
    width: 9px;
    height: 16px;

    path {
      fill: ${Colors.forestGreen};
    }
  }
`;

const OpenCompare = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5px;
  margin: 0;
  background: #0000;
  border: none;
  cursor: pointer;
  transition: 0.2s opacity ease;

  &:hover {
    opacity: 0.6;
  }

  span {
    text-transform: uppercase;
    margin-left: 10px;
    font-family: BrownStd;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: 1.5px;
    text-align: center;
  }
`;
